import { useCallback, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import request from "../utils/request";
import { jwtDecode } from "../utils/jwt";
import { getToken } from "../utils/token";

const NOTIFICATION_URL = process.env.REACT_APP_NOTIFICATION_URL || "http://localhost:6002/hub/notification";
const PAGE = 20;

/**
 * 通知中心 —— 對接後端落地通知（notification 表）＋ SignalR 即時推播。
 *
 * 狀態刻意放在模組層，不是每個呼叫端各自一份 useState。
 *
 * 改版後通知有兩個出口：殼層（頁首的「通知 N」、側欄 badge、右側抽屜）與獨立的
 * /Notifications 頁。兩邊各自開一份 hook 的話，會變成兩條 SignalR 連線、兩份清單
 * ——同一則推播被收兩次（toast 也跟著跳兩次），而且在抽屜裡按了「全部已讀」，
 * 頁面上那一份還是未讀。所以連線與清單只留一份，hook 只負責訂閱它。
 */

/** 未登入 / 登出後的乾淨狀態，也是模組載入時的初值 */
const EMPTY = {
  notifications: [],
  unread: 0,
  connected: false,
  loadingMore: false,
  hasMore: true,
  newCount: 0,
};

let store = EMPTY;
const listeners = new Set();

/*
 * 推播進來時要回呼的呼叫端（殼層要跳 toast）。
 * 用 Set 而不是單一變數：抽屜與 /Notifications 可能同時掛著，
 * 要不要跳 toast 由各自決定，不該互相覆蓋。
 */
const incoming = new Set();

function emit(patch) {
  store = { ...store, ...(typeof patch === "function" ? patch(store) : patch) };
  listeners.forEach((fn) => fn(store));
}

/*
 * 同一輪有兩個呼叫端掛載時（殼層 + /Notifications）只發一次請求：
 * 後發的共用同一個 Promise，不然每次進通知頁都會打兩次一樣的 GET。
 */
let inflight = null;

function fetchInitial() {
  if (inflight) return inflight;
  const token = getToken();
  if (!token) return Promise.resolve();
  inflight = request
    .get("/Notification/List", { skip: 0, take: PAGE }, { headers: { Authorization: `Bearer ${token}` } })
    .then((res) => {
      const data = jwtDecode(res.data)?.data || {};
      const items = data.items || [];
      emit({ notifications: items, unread: data.unread || 0, hasMore: items.length >= PAGE });
    })
    .catch(() => {
      /*
       * 靜默吞掉：通知是輔助資訊，載不回來不該讓整個殼層跳紅字。
       * 這支端點目前在後端還沒有對應的 Controller（api/Web 只有 NotificationHub），
       * 所以實務上多半是 404 —— 畫面會停在空狀態，而不是壞掉。
       */
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

let connection = null;

function connect() {
  if (connection) return;
  const token = getToken();
  if (!token) return;

  const c = new signalR.HubConnectionBuilder()
    .withUrl(NOTIFICATION_URL, { accessTokenFactory: () => getToken() })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  connection = c;

  c.on("ReceiveNotification", (notification) => {
    emit((s) => ({
      notifications: [notification, ...s.notifications],
      unread: s.unread + 1,
      newCount: s.newCount + 1,
    }));
    /* 頁首只會多一個數字，容易整天沒發現；另外請呼叫端跳一則 toast */
    incoming.forEach((fn) => fn(notification));
  });

  c.onreconnecting(() => emit({ connected: false }));
  c.onreconnected(() => {
    emit({ connected: true });
    fetchInitial();
  });
  c.onclose(() => emit({ connected: false }));

  c.start()
    .then(() => emit({ connected: true }))
    .catch((err) => console.error("[Notification] Connection failed:", err));
}

function disconnect() {
  if (connection) {
    connection.stop();
    connection = null;
  }
  /*
   * 最後一個訂閱者離開＝登出（殼層被卸載）。
   * 清單留在模組層會跨帳號，換人登入時會先看到上一個人的通知，所以一起清掉。
   */
  store = EMPTY;
}

function loadMore() {
  if (store.loadingMore || !store.hasMore) return Promise.resolve();
  emit({ loadingMore: true });
  const token = getToken();
  return request
    .get("/Notification/List", { skip: store.notifications.length, take: PAGE }, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => {
      const data = jwtDecode(res.data)?.data || {};
      const older = data.items || [];
      if (older.length === 0) emit({ hasMore: false });
      else emit((s) => ({ notifications: [...s.notifications, ...older], hasMore: older.length >= PAGE }));
    })
    .catch(() => {
      /* 同 fetchInitial：載不到就維持現狀，不要把錯誤推到使用者臉上 */
    })
    .finally(() => emit({ loadingMore: false }));
}

function markAllRead() {
  const token = getToken();
  return request
    .post("/Notification/ReadAll", {}, { headers: { Authorization: `Bearer ${token}` } })
    .then(() => {
      emit((s) => ({
        unread: 0,
        newCount: 0,
        notifications: s.notifications.map((n) => ({ ...n, is_read: true })),
      }));
    })
    .catch(() => {
      /*
       * 失敗時不改本機狀態：把未讀數清成 0 但伺服器沒記下來，
       * 下次重新整理又全部跳回來，使用者會以為是自己看錯。
       */
    });
}

function markRead(ids) {
  if (!ids || ids.length === 0) return Promise.resolve();
  const token = getToken();
  return request
    .post("/Notification/Read", { ids }, { headers: { Authorization: `Bearer ${token}` } })
    .then(() => {
      emit((s) => ({
        notifications: s.notifications.map((n) => (ids.includes(n.id) ? { ...n, is_read: true } : n)),
        unread: Math.max(0, s.unread - ids.length),
      }));
    })
    .catch(() => {
      /* 理由同 markAllRead */
    });
}

export default function useNotification({ onIncoming } = {}) {
  const [snapshot, setSnapshot] = useState(store);

  /*
   * 回呼存進 ref 而不是放進下面那個 effect 的依賴：呼叫端每次 render 都會給一個
   * 新的函式，依賴一變就會重新訂閱，而重新訂閱會把連線收掉再開 ——
   * 重連的空窗期推播直接漏接。
   */
  const onIncomingRef = useRef(onIncoming);
  onIncomingRef.current = onIncoming;

  useEffect(() => {
    const onChange = (s) => setSnapshot(s);
    listeners.add(onChange);

    const handler = (n) => {
      if (onIncomingRef.current) onIncomingRef.current(n);
    };
    incoming.add(handler);

    connect();
    fetchInitial();
    /* 訂閱之前 store 可能已經被別的呼叫端更新過，補一次快照才不會停在舊值 */
    setSnapshot(store);

    return () => {
      listeners.delete(onChange);
      incoming.delete(handler);
      if (listeners.size === 0) disconnect();
    };
  }, []);

  const resetNewCount = useCallback(() => emit({ newCount: 0 }), []);
  /* 相容：歷史通知改由這裡自行載入，保留空函式避免呼叫端報錯 */
  const loadHistory = useCallback(() => {}, []);

  return {
    notifications: snapshot.notifications,
    unread: snapshot.unread,
    connected: snapshot.connected,
    loadHistory,
    loadMore,
    loadingMore: snapshot.loadingMore,
    hasMore: snapshot.hasMore,
    newCount: snapshot.newCount,
    resetNewCount,
    markAllRead,
    markRead,
    refresh: fetchInitial,
  };
}
